import React, { useState, useRef, useLayoutEffect } from 'react';
import './ManageProducts.css';

function ApprovalRowActions({ 
  product, 
  onProductAction, 
  isReadOnly, 
  userRole, 
  userId,
  isDropdownOpen = false,
  onDropdownToggle
}) {
  const [showApproveDropdown, setShowApproveDropdown] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: -9999, right: 12 });
  
  const showDropdown = isDropdownOpen;
  const setShowDropdown = (value) => {
    if (onDropdownToggle) {
      onDropdownToggle(value);
    }
  };
  
  const handleAction = (action) => {
    setShowDropdown(false);
    setShowApproveDropdown(false);
    onProductAction(action, product.id);
  };
  
  const handleApproveAction = (approveType) => {
    setShowApproveDropdown(false);
    onProductAction(approveType, product.id);
  };
  
  const getAvailableActions = () => {
    const actions = [];

    // Vendors can edit products in pending approval; backend keeps the status as
    // pending_approval and stages changes until an admin approves.
    if (userRole === 'vendor' || userRole === 'brand_user') {
      actions.push({
        key: 'edit',
        label: 'Edit',
        primary: true
      });
      return actions;
    }
    
    // Admin actions
    // Review action - always available
    actions.push({ 
      key: 'review', 
      label: 'Review', 
      primary: true 
    });
    
    // Approve action (single action, no dropdown)
    actions.push({ 
      key: 'approve', 
      label: 'Approve'
    });
    
    // Request Changes action
    actions.push({ 
      key: 'request_changes', 
      label: 'Request Changes' 
    });
    
    // Reject action
    actions.push({ 
      key: 'reject', 
      label: 'Reject', 
      destructive: true 
    });
    
    return actions;
  };
  
  const availableActions = getAvailableActions();

  useLayoutEffect(() => {
    if (!showDropdown) {
      return undefined;
    }

    const updatePosition = () => {
      if (!buttonRef.current) {
        return;
      }

      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuHeight = menuRef.current?.offsetHeight || 0;
      const offset = 8;

      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;

      let top;
      if (menuHeight && spaceAbove >= menuHeight + offset) {
        top = Math.max(offset, rect.top - menuHeight - offset);
      } else if (menuHeight && spaceBelow >= menuHeight + offset) {
        top = rect.bottom + offset;
      } else if (menuHeight) {
        if (spaceAbove >= spaceBelow) {
          top = Math.max(offset, rect.top - menuHeight - offset);
        } else {
          top = Math.max(offset, viewportHeight - menuHeight - offset);
        }
      } else {
        top = rect.bottom + offset;
      }

      const right = Math.max(offset, viewportWidth - rect.right);
      setMenuPosition({ top, right });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showDropdown]);
  
  if (availableActions.length === 0) {
    return <span className="noActions">No actions available</span>;
  }

  // Vendors get an Edit button — they can edit their own pending-approval products
  if (userRole === 'vendor' || userRole === 'brand_user') {
    return (
      <div className="productRowActions">
        <button
          onClick={() => handleAction('edit')}
          className="primaryActionButton"
          title="Edit"
        >
          Edit
        </button>
      </div>
    );
  }

  // Admin: show ⋯ dropdown with Approve, Request Changes, Reject
  return (
    <div className="productRowActions">
      <div className="actionDropdown">
        <button
          ref={buttonRef}
          onClick={() => setShowDropdown(!showDropdown)}
          className="dropdownToggle"
          disabled={isReadOnly}
          title="More actions"
        >
          ⋯
        </button>

        {showDropdown && (
          <>
            <div
              className="dropdownOverlay"
              onClick={() => setShowDropdown(false)}
            />
            <div
              ref={menuRef}
              className="dropdownMenu"
              style={{ top: menuPosition.top, right: menuPosition.right }}
            >
              <button
                onClick={() => handleAction('approve')}
                className="dropdownAction"
                disabled={isReadOnly}
              >
                <span className="actionLabel">Approve</span>
              </button>
              <button
                onClick={() => handleAction('request_changes')}
                className="dropdownAction"
                disabled={isReadOnly}
              >
                <span className="actionLabel">Request Changes</span>
              </button>
              <button
                onClick={() => handleAction('reject')}
                className="dropdownAction destructive"
                disabled={isReadOnly}
              >
                <span className="actionLabel">Reject</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ApprovalRowActions;
